import { ref, computed } from 'vue'
import { useQuery } from '@tanstack/vue-query'
const { flux, fluxDuration } = require('@influxdata/influxdb-client')
import {
	INFLUXDB,
	queryApi,
	parseTimeDate,
} from '@/api/influxdb.js'
// import { useStationsPassportHistory } from '@/composables/stationsPassportHistory.js'
import { useAccontStore } from '@/stores/AccountStore.js'
import { useStationsPassport } from './stationsPassport.js'
import {
	dateTimeToRFC3339,
} from '@/helpers.js'
import { Station } from '@/models/station.js'




export const alertSchema = Object.freeze({
	time: Object.freeze({
		key: 'time',
		title: 'час'
	}),
	class: Object.freeze({
		key: 'class',
		title: 'клас',

		allowed: Object.freeze({
			guard: Object.freeze({
				key: 'guard',
				title: 'охорона',
			}),
			protection: Object.freeze({
				key: 'protection',
				title: 'захист',
			}),
			status: Object.freeze({
				key: 'status',
				title: 'стан',
			}),
		}),

		findClass(key) {
			return Object.values(alertSchema.class.allowed)
				.find((el) => el.key === key)
		},
	}),
	counter: Object.freeze({
		key: 'counter',
		title: 'лічильник',
	}),
	stationId: Object.freeze({
		key: 'station_id',
		title: '',
	}),
	type: Object.freeze({
		key: 'type',
		title: 'тип',

		allowed: Object.freeze({
			channel1Activation: Object.freeze({
				key: 'channel_1_activation',
				transition: 'activation',
				title: 'канал 1',
			}),
			channel1Return: Object.freeze({
				key: 'channel_1_return',
				transition: 'return',
				title: 'канал 1 повернувся',
			}),
			channel2Activation: Object.freeze({
				key: 'channel_2_activation',
				transition: 'activation',
				title: 'канал 2',
			}),
			channel2Return: Object.freeze({
				key: 'channel_2_return',
				transition: 'return',
				title: 'канал 2 повернувся',
			}),
			circuit1Activation: Object.freeze({
				key: 'circuit_1_activation',
				transition: 'activation',
				title: 'контур 1',
			}),
			circuit1Return: Object.freeze({
				key: 'circuit_1_return',
				transition: 'return',
				title: 'контур 1 повернувся',
			}),
			circuit2Activation: Object.freeze({
				key: 'circuit_2_activation',
				transition: 'activation',
				title: 'контур 2',
			}),
			circuit2Return: Object.freeze({
				key: 'circuit_2_return',
				transition: 'return',
				title: 'контур 2 повернувся',
			}),
			circuit3Activation: Object.freeze({
				key: 'circuit_3_activation',
				transition: 'activation',
				title: 'контур 3',
			}),
			circuit3Return: Object.freeze({
				key: 'circuit_3_return',
				transition: 'return',
				title: 'контур 3 повернувся',
			}),
			circuit4Activation: Object.freeze({
				key: 'circuit_4_activation',
				transition: 'activation',
				title: 'контур 4',
			}),
			circuit4Return: Object.freeze({
				key: 'circuit_4_return',
				transition: 'return',
				title: 'контур 4 повернувся',
			}),
			circuit5Activation: Object.freeze({
				key: 'circuit_5_activation',
				transition: 'activation',
				title: 'контур 5',
			}),
			circuit5Return: Object.freeze({
				key: 'circuit_5_return',
				transition: 'return',
				title: 'контур 5 повернувся',
			}),
			circuit6Activation: Object.freeze({
				key: 'circuit_6_activation',
				transition: 'activation',
				title: 'контур 6',
			}),
			circuit6Return: Object.freeze({
				key: 'circuit_6_return',
				transition: 'return',
				title: 'контур 6 повернувся',
			}),
			shuntActivation: Object.freeze({
				key: 'shunt_activation',
				transition: 'activation',
				title: 'шунт',
			}),
			shuntReturn: Object.freeze({
				key: 'shunt_return',
				transition: 'return',
				title: 'шунт повернувся',
			}),
			v220Activation: Object.freeze({
				key: 'v220_activation',
				transition: 'activation',
				title: '220 В',
			}),
			v220Return: Object.freeze({
				key: 'v220_return',
				transition: 'return',
				title: '220 В повернувся',
			}),
		}),

		findType(key) {
			return Object.values(alertSchema.type.allowed)
				.find((el) => el.key === key)
		},
	}),
	value: Object.freeze({
		key: 'value',
		title: 'значення',

		allowed: Object.freeze({
			shortCircuit: Object.freeze({
				key: 'short_circuit',
				title: 'КЗ в контурі',
			}),
			circuitConnected: Object.freeze({
				key: 'circuit_connected',
				title: 'контур замкнуто через резистор',
			}),
			circuitDisconnected: Object.freeze({
				key: 'circuit_disconnected',
				title: 'контур розімкнуто',
			}),
			noBreakdown: Object.freeze({
				key: 'no_breakdown',
				title: 'немає аварії',
			}),
			lowThreshold: Object.freeze({
				key: 'low_threshold',
				title: 'вихід за нижню межу',
			}),
			hiThreshold: Object.freeze({
				key: 'hi_threshold',
				title: 'контур вихід за верхню межу',
			}),
			normalStateReturn: Object.freeze({
				key: 'normal_state_return',
				title: 'повернувся до нормального стану',
			}),
		}),

		findValue(key) {
			return Object.values(alertSchema.value.allowed)
				.find((el) => el.key === key)
		},
	}),
	nominal: Object.freeze({
		key: 'nominal',
		title: 'номінал',
	}),
})




export function useAlerts() {
	const accountStore = useAccontStore()

	const {
		passportsMap,
	} = useStationsPassport(
		accountStore.isPrimary ? null : accountStore.account[accountStore.schema.organizationId],
		accountStore.isPrimary ? null : accountStore.departmentsScope,
		accountStore.isPrimary ? null : accountStore.sectorsScope,
	)

	// Reactive lower bound for alerts fetch time window.
	const fromTime = ref(null)
	// Reactive upper bound for alerts fetch time window.
	const toTime = ref(null)
	// Station id filter - array of approved station ids
	const stationIds = ref([])

	const queryFrom = computed(() =>
		fromTime.value instanceof Date
			? dateTimeToRFC3339(fromTime.value)
			: null
	)

	const queryTo = computed(() =>
		toTime.value instanceof Date
			? dateTimeToRFC3339(toTime.value)
			: fluxDuration('-1s')
	)

	// Map for all stations {metricsId: stationId}
	const metricsIdMap = computed(() => {
		const stationMapValue = passportsMap.value
		if (!(stationMapValue instanceof Map)) {
			return new Map()
		}

		return new Map(Array.from(stationMapValue.values()).map(
			(passport) => new Array(passport[Station.passportKeys.metricsId], passport[Station.passportKeys.id])
		))
	})

	const queryMetricsId = computed(() => {
		// const stationMapValue = passportsMap.value
		// if (!(stationMapValue instanceof Map)) {
		// 	return []
		// }

		// return Array.from(stationMapValue.values()).map(
		// 	(passport) => passport[Station.passportKeys.metricsId]
		// )
		return Array.from(metricsIdMap.value.keys())
	})

	const alertsQuery = useQuery({
		// Include auth identity and time filter in key to auto-refetch on change
		queryKey: computed(() => [
			'alerts',
			queryFrom.value,
			queryTo.value,
			...stationIds.value,
		]),
		enabled: computed(() => 
			queryMetricsId.value.length > 0
			&& fromTime.value !== null
			&& toTime.value !== null
		),
		queryFn: async () => {
			const query = flux`
				station_ids_arr = ${queryMetricsId.value}
				from(bucket: ${INFLUXDB.station_metrics.bucket})
					|> range(start: ` + queryFrom.value + flux`, stop: ` + queryTo.value + flux`)
					|> filter(fn: (r) => r["_measurement"] == "${INFLUXDB.station_metrics.alerts}")
					|> filter(fn: (r) => contains(value: r["station_id"], set: station_ids_arr))
					|> sort(columns: ["_time"], desc: true)
					|> pivot(rowKey:["_time"], columnKey: ["_field"], valueColumn: "_value")
			`

			const rows = await queryApi.collectRows(query)

			return rows.map((row) => {
				const {
					// eslint-disable-next-line no-unused-vars
					_measurement, _start, _stop, _time, result, table, station_id,
					...alertRow
				} = row

				return {
					...alertRow,
					station_id: metricsIdMap.value.get(row.station_id),
					time: parseTimeDate(row._time),
				}
			})
		},
		// Keep results warm to reduce repeated network requests.
		staleTime: 10 * 60 * 1000,
	})

	const allAlerts = computed(() => alertsQuery.data.value ?? [])

	// Set period or alert type filters
	function setFilters(
		filters={
			fromTime: null,
			toTime: null,
			stationIds: null,
		}
	) {
		// console.log('setFilters', filters)
		if (filters.fromTime !== null && filters.fromTime instanceof Date) {
			fromTime.value = new Date(filters.fromTime)
		}

		if (filters.toTime !== null && filters.toTime instanceof Date) {
			toTime.value = new Date(filters.toTime)
		}

		if (filters.stationIds !== null && Array.isArray(filters.stationIds)) {
			stationIds.value = [...filters.stationIds]
		}
	}

	return {
		alertSchema,

		allAlerts,
		isLoading: alertsQuery.isLoading,
		isError: alertsQuery.isError,
		error: alertsQuery.error,
		
		setFilters,
	}
}